import React, { useEffect } from 'react';
import styled from 'styled-components';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { IntlShape, injectIntl } from 'react-intl';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardTitle from 'containers/common/Card/CardTitle';
import TablePagination from '@material-ui/core/TablePagination';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { get } from 'lodash';
import { requestClaimRecap } from 'containers/RecapManagerPage/actions';
import { requestSearchClaims } from 'containers/IdentificationPage/actions';
import { translate } from 'containers/services/translate';
import messages from 'containers/UsageClaimsPage/messages';
import { ROW_PER_PAGE } from 'constants/global';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import { requestUsageClaims } from 'containers/UsageClaimsPage/actions';
import {
  makeSelectUsageClaims,
  makeSelectCountUsageClaims,
} from 'containers/UsageClaimsPage/selectors';
import { ClaimByStatus, SagisConfig } from 'containers/AppContainer/typings';
import { formatClaim } from 'containers/AppContainer/utils';
import {
  makeSelectKeycloakToken,
  makeSelectListingsByField,
} from 'containers/AppContainer/selectors';
import { LISTINGS } from 'containers/data-structure';

const Container = styled.div`
   {
    margin-top: 50px;
  }
  .title {
    text-align: left;
    font-family: Roboto;
    font-weight: Bold;
    font-size: 18px;
    letter-spacing: 0;
    color: #384264;
    opacity: 1;
    padding: 10px;
  }
  .headerTitle {
    text-align: left;
    font-size: 11px;
    letter-spacing: 0;
    color: #000000de;
    text-transform: none;
    opacity: 1;
    font-family: Roboto;
    padding-bottom: 0px;
    font-weight: Bold;
  }
  .row {
    border-radius: 4px;
    text-align: left;
    font-family: Roboto;
    letter-spacing: 0;
    font-size: 11px;
    color: #2b2727;
  }
  .claims {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }
  .pendingClaims {
    padding-top: 32px;
    width: 100%;
  }
  .header {
    display: flex;
    justify-content: space-between;
  }
`;

export interface UsageClaimsPageProps {
  intl: IntlShape;
}

const UsageClaimsPage: React.FC<UsageClaimsPageProps> = (
  props,
): JSX.Element => {
  const dispatch = useDispatch();
  const [page, setPage] = React.useState(0);
  const [pageSize, setRowsPerPage] = React.useState(25);

  const countClaims = useSelector(makeSelectCountUsageClaims());
  const usageClaims = useSelector(makeSelectUsageClaims());
  const cities: SagisConfig[] = useSelector(
    makeSelectListingsByField(LISTINGS.CITIES),
  );
  const keyclokToken = useSelector(makeSelectKeycloakToken());
  useEffect(() => {
    if (keyclokToken) {
      dispatch(requestUsageClaims(page, pageSize));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyclokToken]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    dispatch(requestUsageClaims(newPage, pageSize));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
    dispatch(requestUsageClaims(page, size));
  };

  const formattedPendingClaims = formatClaim(usageClaims, cities);

  return (
    <Container>
      <Card>
        <CardContent>
          <div className="header">
            <CardTitle
              count={countClaims}
              message={translate({ ...messages.title }, props)}
            />
          </div>
          <div>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.claimNumber }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.registrationNumber }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.openingDate }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.lossDate }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.claimPlace }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.lossHour }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.numberOfVictim }, props)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formattedPendingClaims.map((row: ClaimByStatus) => (
                  <TableRow
                    key={row.id}
                    onClick={() => {
                      dispatch(
                        requestSearchClaims({
                          claimNumber: get(row, 'claimNumber'),
                        }),
                      );
                      dispatch(requestClaimRecap(get(row, 'id')));
                      pushTo(PAGES.PREOPENING, {
                        claimId: get(row, 'id'),
                        isUsageOpening: true,
                      })();
                    }}
                  >
                    <TableCell align="left" className="row">
                      {row.claimNumber}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.registrationNumber}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {moment(row.createdAt).format('YYYY-MM-DD')}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {moment(row.lossDate).format('YYYY-MM-DD')}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.lossLocationLabel}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.lossHour}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.victims.length}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={ROW_PER_PAGE}
              component="div"
              count={countClaims}
              rowsPerPage={pageSize}
              page={page}
              labelRowsPerPage={translate({ ...messages.linesPerPage }, props)}
              onPageChange={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </div>
        </CardContent>
      </Card>
    </Container>
  );
};

export default injectIntl(UsageClaimsPage);
